import { cn } from '@/lib/utils'
import { IssueStatusCell as BaseStatusCell } from '@/modules/issues/components/table/components'
import { format } from '@/utils/date-fns'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { PropsWithChildren } from 'react'
import React from 'react'
import styled, { css } from 'styled-components'
import type { AbsenceTypeNode } from '../types.graphql'
import type { AbsenceNode } from './absences-table'

interface AbsenceTypeHeaderProps extends PropsWithChildren {
  absenceType: Pick<AbsenceTypeNode, 'id' | 'name'> | null
  onRemove(): void
}

export function AbsenceTypeHeader({
  children,
  absenceType,

  onRemove,
}: AbsenceTypeHeaderProps) {
  return (
    <div>
      {!!absenceType ? (
        <div className={cn('relative flex flex-col')}>
          <span
            className={cn(
              'absolute -top-3 left-0 block text-xs text-gray-400 cursor-pointer hover:text-red-500'
            )}
            role="button"
            onClick={() => onRemove()}
          >
            {children}
          </span>
          {absenceType.name}
        </div>
      ) : (
        children
      )}
    </div>
  )
}

interface DateCellProps {
  date: Date
  side: 'start' | 'end'
  type: 'timeentry' | 'absence'
}

export function DateCell({ date, side, type }: DateCellProps) {
  const timeString = format(date, 'HH:mm')
  const dateString = format(date, 'd. MMM yyyy').toLowerCase()

  return (
    <div className="w-24">
      <div
        className={cn(
          'relative flex flex-col',
          side === 'start'
            ? 'items-start pl-4 md:pl-0'
            : 'items-end pr-4 md:pr-0'
          // "before:content-[''] before:absolute before:h-px before:bg-gray-200",
          // type === 'timeentry' ? 'before:bottom-3.5' : 'before:bottom-2',
          // side === 'start'
          //   ? 'before:-right-4 before:left-22 md:before:left-18'
          //   : 'before:-left-4 before:right-22 md:before:right-18'
        )}
      >
        <span className={cn('relative')}>
          {type === 'timeentry' ? timeString : dateString}
        </span>

        {type === 'timeentry' && (
          <span className={cn('text-xs font-medium text-gray-400')}>
            {dateString}
          </span>
        )}
      </div>
    </div>
  )
}

interface StatusCellProps {
  status: AbsenceNode['requestStatus']
}

export function StatusCell({ status }: StatusCellProps) {
  const translations = useTranslate({
    APPROVED: 'timesheets.absence-approved',
    PENDING: 'timesheets.absence-pending',
    REJECTED: 'timesheets.absence-rejected',
  })

  return (
    <div
      className={cn(
        'text-sm font-semibold',
        "before:content-[''] before:inline-block before:size-2 before:mr-2 before:mb-px before:rounded-full",
        status === 'APPROVED'
          ? 'before:bg-lime-600'
          : status === 'PENDING'
          ? 'before:bg-yellow-500'
          : 'before:bg-red-600'
      )}
    >
      {translations[status]}
    </div>
  )
}

/* DEPRECATED COMPONENTS BELOW */

interface CellProps {
  narrow?: boolean
}
const Cell = styled.td.attrs<CellProps>(props => ({
  width: props.narrow ? '1px' : undefined,
}))<CellProps>`
  font-weight: 600 !important;
`

/* AbsenceType */

interface StyledAbsenceTypeCellProps {
  isSelected: boolean
}
const StyledAbsenceTypeCell = styled(Cell)<StyledAbsenceTypeCellProps>`
  & > div {
    cursor: pointer;

    &:hover {
      color: ${props =>
        props.isSelected
          ? props.theme.colors.red
          : props.theme.colors.secondary};
    }
  }
`

interface OldAbsenceTypeCellProps {
  absenceType?: Pick<AbsenceTypeNode, 'id' | 'name'>
  isSelected: boolean
  onSelect: (absenceType: Pick<AbsenceTypeNode, 'id' | 'name'> | null) => void
}
export const OldAbsenceTypeCell: React.FC<OldAbsenceTypeCellProps> = ({
  absenceType,
  isSelected,
  onSelect,
}) => (
  <StyledAbsenceTypeCell narrow isSelected={isSelected}>
    {!!absenceType && (
      <div
        role="button"
        onClick={() => onSelect(isSelected ? null : absenceType)}
      >
        {absenceType.name}
      </div>
    )}
  </StyledAbsenceTypeCell>
)

/* DateCell */

interface StyledOldDateCellProps {
  side: 'start' | 'end'
  type: 'timeentry' | 'absence'
}
const StyledOldDateCell = styled(Cell)<StyledOldDateCellProps>`
  width: 98px;
  & > div {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    span {
      &.time {
        position: relative;
      }
      &.date {
        font-size: 0.8rem;
        font-weight: 500 !important;
        color: ${props => props.theme.colors.gray6};
      }
    }
  }

  & > div::before {
    content: '';
    position: absolute;
    display: block;
    height: 1px;
    bottom: ${props => (props.type === 'timeentry' ? '15px' : '8px')};
    background: ${props => props.theme.colors.gray8};
  }
  ${props =>
    props.side === 'start'
      ? css`
          & > div::before {
            left: 4.5rem;
            right: -1rem;
            ${props => props.theme.media.mobile} {
              left: 5.5rem;
            }
          }
        `
      : css`
          & > div::before {
            left: -1rem;
            right: 4.5rem;
            ${props.theme.media.mobile} {
              right: 5.5rem;
            }
          }
        `}

  ${props =>
    props.side === 'start'
      ? css`
          ${props => props.theme.media.mobile} {
            & > div {
              padding-left: 1rem;
            }
          }
        `
      : css`
          & > div {
            align-items: flex-end;
          }

          ${props => props.theme.media.mobile} {
            & > div {
              padding-right: 1rem;
            }
          }
        `};
`

interface OldDateCellProps {
  date: Date
  side: 'start' | 'end'
  type: 'timeentry' | 'absence'
}
export const OldDateCell: React.FC<OldDateCellProps> = ({
  date,
  side,
  type,
}) => {
  const timeString = format(date, 'HH:mm')
  const dateString = format(date, 'd. MMM').toLowerCase()

  return (
    <StyledOldDateCell narrow side={side} type={type}>
      <div>
        <span className="time">
          {type === 'timeentry' ? timeString : dateString}
        </span>
        {type === 'timeentry' && <span className="date">{dateString}</span>}
      </div>
    </StyledOldDateCell>
  )
}

/* AbsenceTypeHeader */

interface StyledAbsenceTypeHeaderProps {
  selected: boolean
}
const StyledAbsenceTypeHeader = styled.th<StyledAbsenceTypeHeaderProps>`
  ${props =>
    props.selected &&
    css`
      padding-top: 0.2rem !important;

      div {
        display: flex;
        flex-direction: column;

        span {
          display: block;
          height: 1rem;

          font-size: 0.7rem;
          color: ${props => props.theme.colors.gray5};
          cursor: pointer;

          &:hover {
            color: ${props => props.theme.colors.red};
          }
        }
      }
    `};
`

interface OldAbsenceTypeHeaderProps {
  absenceType: Pick<AbsenceTypeNode, 'id' | 'name'> | null
  text: string
  onRemove: () => void
}
export const OldAbsenceTypeHeader: React.JSX.CFC<OldAbsenceTypeHeaderProps> = ({
  absenceType,
  text,
  onRemove,
}) => (
  <StyledAbsenceTypeHeader selected={!!absenceType}>
    {absenceType ? (
      <div>
        <span onClick={() => onRemove()}>{text}</span>
        {absenceType.name}
      </div>
    ) : (
      text
    )}
  </StyledAbsenceTypeHeader>
)
/* Absence Status */

const OldStyledStatusCell = styled(BaseStatusCell)`
  padding-right: 1rem;
`
interface OldAbsenceStatusProps {
  requestStatus: string | undefined
}
export const OldAbsenceStatusCell: React.FC<OldAbsenceStatusProps> = ({
  requestStatus,
}) => {
  const translations = useTranslate({
    approved: 'timesheets.absence-approved',
    pending: 'timesheets.absence-pending',
    rejected: 'timesheets.absence-rejected',
  })
  if (!requestStatus) return <Cell narrow>-</Cell>
  const pending = requestStatus === 'PENDING'
  const approved = requestStatus === 'APPROVED'
  return (
    <OldStyledStatusCell
      closed={approved}
      archived={pending}
      openText={translations.rejected}
      archivedText={translations.pending}
      closedText={translations.approved}
      edited={false}
      editText=""
    />
  )
}
